import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Book Download Button Component
const AdminBookDownloadButton = ({ book }) => {
  const [downloading, setDownloading] = useState({});

  const handleDownload = async (format) => {
    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      
      console.log(`[ADMIN DOWNLOAD] Starting ${format} download for book:`, book.id);
      
      const response = await fetch(`${API}/admin/books/${book.id}/download/${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log(`[ADMIN DOWNLOAD] Response status:`, response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Download failed' }));
        throw new Error(errorData.detail || `Failed to download ${format.toUpperCase()}`);
      }

      const blob = await response.blob();
      console.log(`[ADMIN DOWNLOAD] Blob created - size: ${blob.size} bytes, type: ${blob.type}`);
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      // Create download URL
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.title.replace(/[^a-zA-Z0-9\s]/g, '')}.${format}`;
      a.style.display = 'none';
      
      // Add to DOM and trigger
      document.body.appendChild(a);
      a.click();
      
      // Clean up after download should have started
      setTimeout(() => {
        try {
          if (document.body.contains(a)) {
            document.body.removeChild(a);
          }
          window.URL.revokeObjectURL(url);
          console.log(`[ADMIN DOWNLOAD] Cleanup completed`);
        } catch (cleanupError) {
          console.warn(`[ADMIN DOWNLOAD] Cleanup error:`, cleanupError);
        }
      }, 2000);
      
      alert(`📥 ${format.toUpperCase()} download started! Check your browser's downloads folder.`);
      console.log(`[ADMIN DOWNLOAD] Download process completed successfully`);
      
    } catch (error) {
      console.error('[ADMIN DOWNLOAD] Download failed:', error);
      alert(`❌ Download failed: ${error.message}`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={() => handleDownload('pdf')}
        disabled={downloading.pdf}
        className="w-full bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {downloading.pdf ? (
          <>⏳ PDF...</>
        ) : (
          <>📕 PDF</>
        )}
      </button>
      
      <button
        onClick={() => handleDownload('docx')}
        disabled={downloading.docx}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {downloading.docx ? (
          <>⏳ DOCX...</>
        ) : (
          <>📄 DOCX</>
        )}
      </button>
      
      <button
        onClick={() => handleDownload('epub')}
        disabled={downloading.epub}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {downloading.epub ? (
          <>⏳ EPUB...</>
        ) : (
          <>📚 EPUB</>
        )}
      </button>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [generatingBooks, setGeneratingBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [adminPrompt, setAdminPrompt] = useState('');
  const [adminCategory, setAdminCategory] = useState('Fiction');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadDashboardData();
  }, [navigate]);

  // Polling effect for generating books
  useEffect(() => {
    let interval;
    
    if (generatingBooks.length > 0) {
      // Poll every 3 seconds when there are generating books
      interval = setInterval(() => {
        loadGeneratingBooks();
      }, 3000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [generatingBooks.length]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsResponse, usersResponse, booksResponse] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/books`, { headers })
      ]);

      setStats(statsResponse.data);
      setUsers(usersResponse.data.users || []); // Ensure users is always an array
      setBooks(booksResponse.data.books || []); // Ensure books is always an array
      
      // Also load generating books
      await loadGeneratingBooks();
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadGeneratingBooks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log('🔄 [ADMIN] Loading generating books...');
      const generatingResponse = await axios.get(`${API}/admin/generating-books`, { headers });
      const generatingBooks = generatingResponse.data.generating_books || [];
      
      console.log(`📊 [ADMIN] Found ${generatingBooks.length} generating books:`, generatingBooks);
      setGeneratingBooks(generatingBooks);
      
      return generatingBooks;
    } catch (error) {
      console.error('❌ [ADMIN] Error loading generating books:', error.message);
      console.error('Error details:', error.response?.data);
      setGeneratingBooks([]);
      return [];
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-purple-600 hover:text-purple-800 font-medium"
              >
                View Store
              </button>
              <button
                onClick={handleLogout}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm font-medium text-gray-500">Total Users</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total_users}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm font-medium text-gray-500">Total Books</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total_books}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm font-medium text-gray-500">Generated Books</div>
              <div className="text-2xl font-bold text-gray-900">{stats.generated_books}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm font-medium text-gray-500">Active Subscriptions</div>
              <div className="text-2xl font-bold text-gray-900">{stats.active_subscriptions}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {['overview', 'users', 'books'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && stats && (
          <div>
            {/* System Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <span className="text-2xl">📚</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Books</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.total_books}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <span className="text-2xl">👥</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.total_users}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <span className="text-2xl">📄</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Exports</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.total_exports}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <span className="text-2xl">⚡</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Recent Books (24h)</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.recent_books}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Cleanup Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">🧹</span>
                  System Cleanup
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Delete all books, exports, and progress records to start fresh with the new persistent storage system.
                </p>
                <button
                  onClick={async () => {
                    const confirmed = window.confirm(
                      '🚨 DANGER: This will delete ALL books, exports, and progress records from the system. This action CANNOT be undone.\n\nAre you absolutely sure you want to proceed?'
                    );
                    
                    if (confirmed) {
                      const doubleConfirm = window.confirm(
                        '⚠️ FINAL CONFIRMATION: You are about to delete EVERYTHING. Type "DELETE ALL" in the next prompt to confirm.'
                      );
                      
                      if (doubleConfirm) {
                        const verification = window.prompt('Type "DELETE ALL" to confirm:');
                        if (verification === 'DELETE ALL') {
                          try {
                            setLoading(true);
                            const token = localStorage.getItem('admin_token');
                            const response = await axios.delete(`${API}/admin/cleanup/all-books`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            
                            alert('✅ Complete system cleanup completed successfully!\n\n' + 
                                  `Deleted: ${response.data.deleted.books} books, ${response.data.deleted.exports} exports, ` +
                                  `${response.data.deleted.progress_records} progress records`);
                            
                            loadDashboardData(); // Refresh data
                          } catch (error) {
                            alert('❌ Cleanup failed: ' + (error.response?.data?.detail || error.message));
                          } finally {
                            setLoading(false);
                          }
                        } else {
                          alert('Verification failed. Cleanup cancelled.');
                        }
                      }
                    }
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium"
                  disabled={loading}
                >
                  🗑️ Delete All Content
                </button>
              </div>

              {/* Admin Book Generation */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">⚡</span>
                  Admin Book Generation
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Generate books as admin without credit restrictions. Perfect for testing the new system.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Book Prompt
                    </label>
                    <textarea
                      id="admin-book-prompt"
                      value={adminPrompt}
                      onChange={(e) => setAdminPrompt(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      rows="3"
                      placeholder="Enter your book idea here..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      id="admin-book-category"
                      value={adminCategory}
                      onChange={(e) => setAdminCategory(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="Fiction">Fiction</option>
                      <option value="Textbook">Textbook</option>
                      <option value="Children's Book">Children's Book</option>
                      <option value="Cookbook">Cookbook</option>
                      <option value="Non-Fiction">Non-Fiction</option>
                    </select>
                  </div>
                  
                  <button
                    onClick={async () => {
                      const prompt = adminPrompt.trim();
                      const category = adminCategory;
                      
                      if (!prompt) {
                        alert('Please enter a book prompt');
                        return;
                      }
                      
                      try {
                        setLoading(true);
                        const token = localStorage.getItem('admin_token');
                        const response = await axios.post(`${API}/admin/generate-book`, {
                          prompt: prompt,
                          category: category
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        
                        console.log('✅ [ADMIN] Book generation started:', response.data);
                        alert('✅ Admin book generation started!\n\nBook ID: ' + response.data.book_id + '\n\nGeneration progress will appear in the Books tab.');
                        
                        // Clear form using React state
                        setAdminPrompt('');
                        
                        // Switch to Books tab to show progress
                        console.log('📚 [ADMIN] Switching to Books tab...');
                        setActiveTab('books');
                        
                        // Refresh dashboard data to show generating book
                        console.log('🔄 [ADMIN] Refreshing dashboard data...');
                        await loadDashboardData();
                        
                        // Also manually load generating books with a small delay to ensure backend has updated
                        setTimeout(async () => {
                          console.log('🔄 [ADMIN] Manual refresh of generating books...');
                          await loadGeneratingBooks();
                        }, 2000);
                        
                      } catch (error) {
                        console.error('❌ [ADMIN] Generation error:', error);
                        alert('❌ Generation failed: ' + (error.response?.data?.detail || error.message));
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium"
                    disabled={loading}
                  >
                    📚 Generate Book (No Credits Required)
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 System Status</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{stats.book_types?.fiction || 0}</p>
                  <p className="text-sm text-gray-600">Fiction Books</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.book_types?.textbook || 0}</p>
                  <p className="text-sm text-gray-600">Textbooks</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{stats.user_plans?.storage || 0}</p>
                  <p className="text-sm text-gray-600">Storage Users</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{stats.user_plans?.free || 0}</p>
                  <p className="text-sm text-gray-600">Free Users</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Users</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Books
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(users || []).map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.plan === 'storage' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.plan}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.credits}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.book_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'books' && (
          <div>
            {/* Currently Generating Books Section */}
            {generatingBooks.length > 0 ? (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">⚡</span>
                  Currently Generating ({generatingBooks.length})
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
                  {generatingBooks.map((genBook) => (
                    <div key={`generating-${genBook.book_id}`} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow-sm overflow-hidden border-2 border-purple-200">
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 text-sm">{genBook.title}</h3>
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                            {genBook.generator_type}
                          </span>
                        </div>
                        
                        <p className="text-xs text-gray-600 mb-3">
                          User: {genBook.user_id === 'admin-user' ? 'Admin' : genBook.user_id}
                        </p>
                        
                        {/* Progress Bar */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-700">{genBook.progress}%</span>
                            <span className="text-xs text-gray-500">{genBook.status}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${genBook.progress}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-600 mb-2">
                          {genBook.current_step}
                        </p>
                        
                        <p className="text-xs text-gray-400">
                          Started: {new Date(genBook.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mb-8">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-gray-600 text-sm">
                    🕐 No books currently generating. Start a generation above to see progress here.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Progress updates every 3 seconds automatically.
                  </p>
                </div>
              </div>
            )}

            {/* Admin Books Section - Using same layout as user dashboard */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📚</span>
                Completed Books ({(books || []).length})
              </h3>
            </div>

            {/* Books Grid - Same as user dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {(books || []).map((book) => (
                <div key={`admin-book-${book.id}`} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <img
                    src={
                      book.cover_image_url?.startsWith('http') 
                        ? book.cover_image_url 
                        : book.cover_image_url?.startsWith('/api')
                        ? `${BACKEND_URL}${book.cover_image_url}`
                        : `${API}${book.cover_image_url}`
                    }
                    alt={book.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=600&fit=crop';
                    }}
                  />
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                    <p className="text-xs text-gray-500">{book.category} • {book.generator_type}</p>
                    
                    {/* Admin-specific info */}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-400">
                        User: {book.user_email}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        book.has_content 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {book.has_content ? '✅ Has Content' : '❌ Empty'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-400">
                        Created: {new Date(book.created_at).toLocaleDateString()}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        book.has_exports 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {book.has_exports ? `📄 ${book.export_formats.length} formats` : '📄 No exports'}
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      <button
                        onClick={() => window.open(`/book/${book.id}`, '_blank')}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium"
                      >
                        View Details
                      </button>
                      
                      {/* Use the same BookDownloadButton component as user dashboard */}
                      <AdminBookDownloadButton book={book} />
                      
                      {/* Admin-only actions */}
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              setLoading(true);
                              const token = localStorage.getItem('admin_token');
                              await axios.post(`${API}/admin/books/${book.id}/regenerate-exports`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              alert('✅ Exports regenerated successfully!');
                              loadDashboardData(); // Refresh data
                            } catch (error) {
                              alert('❌ Error regenerating exports: ' + (error.response?.data?.detail || error.message));
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium"
                          disabled={loading}
                        >
                          🔄 Regenerate
                        </button>
                        
                        <button
                          onClick={async () => {
                            if (confirm(`Are you sure you want to delete "${book.title}"? This action cannot be undone.`)) {
                              try {
                                setLoading(true);
                                const token = localStorage.getItem('admin_token');
                                await axios.delete(`${API}/admin/books/${book.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                alert('✅ Book deleted successfully!');
                                loadDashboardData(); // Refresh data
                              } catch (error) {
                                alert('❌ Error deleting book: ' + (error.response?.data?.detail || error.message));
                              } finally {
                                setLoading(false);
                              }
                            }
                          }}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium"
                          disabled={loading}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty state */}
            {(!books || books.length === 0) && !loading && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No books found</h3>
                <p className="text-gray-600">Books generated by users will appear here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;